import { format, parseISO } from 'date-fns';
import { memo, useMemo } from 'react';
import { Pressable } from 'react-native';

import { imgEmptyActivities } from '@/assets/images';
import { Stack } from '@/components/layout';
import { Image, SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';
import FamilyAgendaRow from './row';
import type { FamilyAgendaSectionProps } from './types';
import { getUpcomingFamilyAgendaItems, groupFamilyAgendaByDate } from './utils';

const strings = STRINGS.guardian.kids;

function FamilyAgendaSection({
  items = [],
  onItemPress,
  onCalendarPress,
  showCalendarLink = true,
  showEmptyState = false,
}: FamilyAgendaSectionProps) {
  const { scale } = useScaling();
  const styles = useStyles();

  const todayKey = format(new Date(), 'yyyy-MM-dd');
  const groups = useMemo(
    () => groupFamilyAgendaByDate(getUpcomingFamilyAgendaItems(items, parseISO(todayKey))),
    [items, todayKey],
  );

  return (
    <Stack width="100%" paddingX={scale(20)} paddingTop={scale(16)} gap={scale(12)}>
      <Stack direction="row" align="center" justify="space-between">
        <Text variant="Font Guardian App/Body/Body 16 Bold" style={styles.title}>
          {strings.familyAgenda}
        </Text>
        {showCalendarLink ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={strings.calendar}
            onPress={onCalendarPress}
            style={({ pressed }) => [
              styles.calendarLink,
              pressed ? styles.calendarLinkPressed : null,
            ]}>
            <Text
              variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
              style={styles.calendarText}>
              {strings.calendar}
            </Text>
            <SvgIcon name="IcChevronRight" size={scale(16)} color={guardianColors['Main/Main']} />
          </Pressable>
        ) : null}
      </Stack>

      {groups.length === 0 && showEmptyState ? (
        <Stack
          height={scale(288)}
          align="center"
          justify="center"
          gap={scale(16)}
          style={styles.emptyCard}>
          <Image
            source={imgEmptyActivities}
            width={scale(200)}
            height={scale(141)}
            resizeMode="contain"
          />
          <Text variant="Font Guardian App/Subtitle/Subtitle 14 Semibold" style={styles.emptyText}>
            {strings.noFamilyAgendaEvents}
          </Text>
        </Stack>
      ) : (
        groups.map(group => (
          <Stack key={group.key} gap={scale(8)}>
            <Text
              variant="Font Guardian App/Subtitle/Subtitle 12 Semibold"
              style={styles.dateLabel}>
              {group.label}
            </Text>
            <Stack overflow="hidden" style={styles.groupCard}>
              {group.items.map((item, index) => (
                <FamilyAgendaRow
                  key={item.id}
                  item={item}
                  isLast={index === group.items.length - 1}
                  onPress={onItemPress}
                />
              ))}
            </Stack>
          </Stack>
        ))
      )}
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  title: {
    color: guardianColors['10'],
  },
  calendarLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: scale(2),
  },
  calendarLinkPressed: {
    opacity: 0.72,
  },
  calendarText: {
    color: guardianColors['Main/Main'],
  },
  dateLabel: {
    color: guardianColors['6'],
  },
  groupCard: {
    borderRadius: scale(12),
    backgroundColor: colors['0'],
  },
  emptyCard: {
    borderRadius: scale(12),
    backgroundColor: colors['0'],
  },
  emptyText: {
    color: guardianColors['5'],
    textAlign: 'center',
  },
}));

export type { FamilyAgendaItem, FamilyAgendaSectionProps } from './types';
export default memo(FamilyAgendaSection);
